/**
 * `/gallery/week-strip` exists so `tests/e2e/src/app/weekStripGeometry.e2e.ts`
 * can measure ISSUE-963's week marker in a real browser. It is signed in and
 * linked from no navigation, following the `/gallery/score-rows` precedent.
 *
 * The file is named `gallery_.week-strip` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import type { typeSeasonWeek } from "../features/scores/seasonWeek";
import { WeekStrip } from "../features/scores/WeekStrip";

/** This page has nowhere to send a selected week; what it needs from the
 *  prop is the real `onClick` handler, not what it does. */
const selectNothing = (): void => undefined;

const SINGLE_DIGIT_WEEKS: readonly typeSeasonWeek[] = [
  { seasonWeek: 6, firstDay: "2026-10-08", lastDay: "2026-10-12" },
  { seasonWeek: 7, firstDay: "2026-10-15", lastDay: "2026-10-19" },
  { seasonWeek: 8, firstDay: "2026-10-22", lastDay: "2026-10-26" },
];

const TWO_DIGIT_WEEKS: readonly typeSeasonWeek[] = [
  { seasonWeek: 9, firstDay: "2026-10-29", lastDay: "2026-11-02" },
  { seasonWeek: 10, firstDay: "2026-11-05", lastDay: "2026-11-09" },
  { seasonWeek: 11, firstDay: "2026-11-12", lastDay: "2026-11-16" },
];

const WeekStripGalleryScreen = (): ReactNode => (
  <div className="flex flex-col gap-6 px-page-gutter py-4">
    <h1 className="text-h1 text-ink">Week strip gallery</h1>
    <p className="text-body text-ink-secondary">
      The week strip with a one digit week and a two digit week selected. This
      page exists so a browser can measure the selected marker. It is not linked
      from any navigation.
    </p>

    <div
      data-testid="week-strip-gallery-single"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">One digit week selected</h2>
      <WeekStrip
        weeks={SINGLE_DIGIT_WEEKS}
        selectedWeek={7}
        onSelectWeek={selectNothing}
      />
    </div>

    <div
      data-testid="week-strip-gallery-double"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">Two digit week selected</h2>
      <WeekStrip
        weeks={TWO_DIGIT_WEEKS}
        selectedWeek={10}
        onSelectWeek={selectNothing}
      />
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/week-strip")({
  beforeLoad: requireSignedIn,
  component: WeekStripGalleryScreen,
});
